import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import { CustomWindowService } from 'app/core/services/custom-window-service/custom-window.service';
import { ObjektId } from '@models/objekt-id';
import { MergeDialogComponent } from '../../merge-dialog.component';
import * as MergeActions from './merge.actions';
import * as MergeSelectors from './merge.selectors';
import { DossierService } from 'app/modules/dossier-area/services/dossier/dossier.service';
import * as DossierActions from 'app/modules/dossier-area/pages/dossier/store/dossier.actions';
import { IAppState } from 'app/modules/shared-area/root-state';

@Injectable()
export class MergeEffects {
  constructor(
    private actions$: Actions,
    private store: Store<IAppState>,
    private i18n: I18nService,
    private windowService: CustomWindowService,
    private dossierService: DossierService,
  ) {}

  showCollisions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MergeActions.showCollisions),
      tap(() => {
        this.windowService.open({
          title: this.i18n.getLocalizedString('components.merge.title'),
          content: MergeDialogComponent,
          width: 750,
        });
      }),
      map(action => action.data[0]),
      switchMap(id =>
        this.dossierService.checkIdentity(id.id).pipe(
          map(result => MergeActions.showCollisionsSuccess({ result })),
          catchError(error => of(MergeActions.showCollisionsError())),
        ),
      ),
    )
  );

  merge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MergeActions.merge),
      withLatestFrom(
        this.store.select(MergeSelectors.getId),
        this.store.select(MergeSelectors.getResult),
        this.store.select(MergeSelectors.getAdditionalId)
      ),
      switchMap(([action, id, result, additionalId]) => {
        const ids = result.collisions.map(c => c.objektId.toString());
        if (additionalId) {
          ids.push(additionalId.toString());
        }
        return this.dossierService.merge(id.id, ids).pipe(
          map(() => MergeActions.mergeSuccess()),
          catchError(() => of(MergeActions.mergeFailed())),
        );
      }),
    )
  );

  mergeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MergeActions.mergeSuccess),
      withLatestFrom(this.store.select(MergeSelectors.getId)),
      tap(([payload, id]) => {
        this.windowService.close();
      }),
      map(([payload, id]) =>
        DossierActions.loadSubstance({
          loadSubstance: { id: id.id },
        }),
      ),
    )
  );
}
